import { isScope, type PermissionKey, type Scope, type ThemePreference } from '@erp/shared';
import { useQuery, type QueryClient } from '@tanstack/react-query';

import { api, ApiError, setCsrfToken } from './api.ts';

export interface Session {
  user: { id: string; email: string; fullName: string };
  /** Saved theme preference (during an impersonation, the member's: never applied). */
  theme: ThemePreference;
  tenant: { id: string; name: string; timezone: string };
  membership: {
    id: string;
    role: { id: string; name: string } | null;
    /** Work phone shown to clients (E.164), or null. */
    workPhone: string | null;
    /** Gets the daily summary (Mi cuenta). */
    dailySummary: boolean;
  };
  teamId: string | null;
  mfa: 'verified' | 'verify' | 'enroll';
  mfaEnrolled: boolean;
  /** "Recordar este dispositivo" at the 2FA step: the agency's term in days (null: off). */
  trustedDeviceDays: number | null;
  permissions: string[];
  impersonation: {
    by: { userId: string; displayName: string | null };
    reason: string;
    expiresAt: string;
  } | null;
  csrfToken: string;
  expiresAt: string;
}

export const SESSION_KEY = ['session'] as const;

/** Reads GET /auth/session. null means "not signed in" (not an error). */
export async function fetchSession(): Promise<Session | null> {
  try {
    const session = await api<Session>('/auth/session');
    setCsrfToken(session.csrfToken);
    return session;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      setCsrfToken(null);
      return null;
    }
    throw error;
  }
}

/**
 * Loads the session NOW and stores it, before navigating after a login or 2FA step.
 * (Invalidating is not enough: an unused query is only marked stale, and the next
 * page would first render the previous cached value, e.g. "no session".)
 */
export async function refreshSession(client: QueryClient): Promise<Session | null> {
  return client.query({ queryKey: SESSION_KEY, queryFn: fetchSession, staleTime: 0 });
}

export function useSession() {
  return useQuery({ queryKey: SESSION_KEY, queryFn: fetchSession, staleTime: 60_000 });
}

/**
 * Scope granted for a permission ('own' | 'team' | 'all'), null for an unscoped
 * permission that is granted, undefined if not granted at all.
 */
export function grantOf(
  permissions: readonly string[],
  key: PermissionKey,
): Scope | null | undefined {
  for (const grant of permissions) {
    const [k, scope] = grant.split(':');
    if (k !== key) continue;
    return scope && isScope(scope) ? scope : null;
  }
  return undefined;
}

/**
 * Whether the signed-in user has a permission (at any scope). Used to hide actions
 * that would be refused anyway; the API is what actually enforces permissions.
 */
export function can(session: Session | null | undefined, key: PermissionKey): boolean {
  return session ? grantOf(session.permissions, key) !== undefined : false;
}

export function useCan(key: PermissionKey): boolean {
  return can(useSession().data, key);
}

export function useScope(key: PermissionKey): Scope | null | undefined {
  const { data } = useSession();
  return data ? grantOf(data.permissions, key) : undefined;
}
